import React from "react";
import { useFormContext } from "react-hook-form";
import { PrimaryButton, Spinner, useNotifications } from "sprint-common-web";

import { useMapToSubmissionsHistoryContent } from "../../../../../../common/content/buttons/lookups/lookup-history/mapToSubmissionsHistoryContent";
import { useAssociateHistory } from "../../../../../../quality-control/useAssociateHistory";
import { useCloudFileDashboard } from "../../../../common/context/useCloudFileDashboard";
import { CloudFileAssociationFormState } from "../../context/CloudFileAssociationContext";
import { useCloudFileAssociationSection } from "../../context/useCloudFileAssociationSection";
import { useSaveCloudFileAssociationProgress } from "./useSaveCloudFileAssociationProgress";

export const SaveCloudFileAssociationProgressButton = (): JSX.Element => {
    const {
        disableEdit,
        enableEdit,
        isEditDisabled,
        isFormValid,
        applyChanges,
        setDefaultFormValues
    } = useCloudFileAssociationSection();

    const { areUnsavedChanges, setAreUnsavedChanges } = useCloudFileDashboard();

    const { handleSubmit, getValues, reset } = useFormContext<CloudFileAssociationFormState>();
    const associateHistory = useAssociateHistory();

    const { success, error } = useNotifications();

    const { saveCloudFile, isRequestPending } = useSaveCloudFileAssociationProgress();

    const { mapToSubmissionsHistoryContent } = useMapToSubmissionsHistoryContent();

    const save = async () => {
        try {
            disableEdit();

            await saveCloudFile();

            applyChanges();

            const formState = getValues();

            const historyEntry = await mapToSubmissionsHistoryContent(formState);

            if (historyEntry) associateHistory.save(historyEntry);

            setDefaultFormValues(formState);

            reset(formState);

            setAreUnsavedChanges(false);

            success("Changes saved");

            enableEdit();
        } catch (e) {
            error(`Error occurred while saving changes: ${e}`);

            enableEdit();
        }
    };

    if (isRequestPending) {
        return <Spinner />;
    }

    return (
        <PrimaryButton
            text="Save"
            onClick={handleSubmit(save)}
            disabled={isEditDisabled || !isFormValid || !areUnsavedChanges || isRequestPending}
            testId="save-cloud-file-button"
        />
    );
};
